/**
 * 🔒 One per-user preference store, never four (piece 12). A saved list view, a
 * remembered page size, the dashboard arrangement and the sidebar's collapsed
 * state are all `SavedView` rows keyed on the person; Composite and data-display
 * components and Modal and overlay system stub it and read the real table here.
 */
import type { SortState } from '../composites/listViewer';
import { VIEW_COMPARATORS, type CriteriaRow } from '../modals/newViewModal';

/**
 * 🔒 The shapes a stored row's three free-form columns take (T2171). The server keeps them
 * as JSON and a client may write any of them, so the wire is `unknown` and ONE reader per
 * column (`readSavedCriteria`, `readSavedSort`, `readSavedLayout`) validates it into these
 * narrow types; nothing downstream casts. A layout carries whichever keys its scope uses:
 * the navigation bar's `collapsed` and `groups`, the dashboard's `order` and `hidden`, a
 * list's remembered `content`. My Jobs' own remembered opening, `workOrderIds` and `openedAt` (decision
 * 3072), is not among the keys here: `readMyJobsSeen` in `documents/myJobs.ts` reads it from the raw
 * layout itself and is the one reader of those two (T2376). The phone's `home` and `bar` (decision 9340) are
 * read the same way, by `readPhoneSlots` below and nothing else.
 */
export interface SavedLayout {
  collapsed?: boolean;
  groups?: Readonly<Record<string, boolean>>;
  order?: readonly string[];
  hidden?: readonly string[];
  content?: string;
  /** 🔒 The list panes' one width (decision 7781): the pane's share of the frame, above nought and below one. */
  paneShare?: number;
}

export interface SavedViewRow {
  id: string;
  ownerPersonId: string;
  /** What the row belongs to: a list page, a document's Item Table, the dashboard, or the navigation bar. */
  scopeKey: string;
  name?: string | null;
  columns?: readonly string[] | null;
  criteria?: readonly CriteriaRow[] | null;
  sort?: SortState | null;
  pageSize?: number | null;
  layout?: SavedLayout | null;
  isDefault?: boolean;
  isFavourite?: boolean;
}

/** The scopes this piece names; a list page's scope is its own route. */
export const NAV_SCOPE_KEY = 'nav';
export const DASHBOARD_SCOPE_KEY = 'dashboard';
/** 🔒 Decision 3072: My Jobs' last opening, the open jobs it showed then, so a job put on the board since reads New. */
export const MY_JOBS_SEEN_SCOPE_KEY = 'myJobs:seen';

/**
 * 🔒 Decision 9340 (row T3131): the phone's own Home quick actions and bar slots, one row per person. Its layout
 * carries `home` and `bar`, each an ordered list of the phone's page keys; a list the layout does not carry reads
 * as the phone's default, so an absent row and an emptied one both read the plan's Home and bar.
 */
export const PHONE_SLOTS_SCOPE_KEY = 'phoneSlots';
/** 🔒 What Home's one row of tiles holds at 390 points (decision 9340). */
export const PHONE_HOME_SLOTS_MAX = 4;
/** 🔒 The bar's slots between Home and More, which stay fixed at its two ends (decision 9340). */
export const PHONE_BAR_SLOTS_MAX = 3;

export type PhoneSlotSection = 'home' | 'bar';

/** The person's arrangement, each list null where the layout carries none and the default stands. */
export interface PhoneSlots {
  home: readonly string[] | null;
  bar: readonly string[] | null;
}

/**
 * 🔒 The one reader of the `phoneSlots` layout (decision 9340; T2171): each list keeps its strings in order, once
 * each, where the caller offers them (a page the viewer's role hides, or one with no phone screen, is dropped), up
 * to its section's ceiling. A list that is not a list is none, and the default stands.
 */
export function readPhoneSlots(row: LayoutBearing | null | undefined, offered: (key: string, section: PhoneSlotSection) => boolean = () => true): PhoneSlots {
  const raw = row?.layout;
  const layout = raw !== null && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const listOf = (section: PhoneSlotSection, ceiling: number): string[] | null => {
    const keys = stringsOf(layout[section]);
    if (!keys) return null;
    return [...new Set(keys)].filter((key) => offered(key, section)).slice(0, ceiling);
  };
  return { home: listOf('home', PHONE_HOME_SLOTS_MAX), bar: listOf('bar', PHONE_BAR_SLOTS_MAX) };
}

/** The layout a Save writes: each list the person arranged, and nothing for a list left at the default. */
export function phoneSlotsLayout(slots: PhoneSlots): { home?: string[]; bar?: string[] } {
  return { ...(slots.home ? { home: [...slots.home] } : {}), ...(slots.bar ? { bar: [...slots.bar] } : {}) };
}

export function listScopeKey(route: string): string {
  return `list:${route}`;
}

/**
 * 🔒 The list pane's own scope on a list-and-detail page (decision 0408), one
 * row per person and per list. It is held apart from the list's own scope
 * because that row already carries the board-or-map choice, and a write of one
 * preference never overwrites the other.
 */
export function listPaneScopeKey(route: string): string {
  return `listPane:${route}`;
}

/**
 * 🔒 The list panes' one width (decision 7781; T3163): one row per person for every list page, held
 * apart from each list's collapsed row so a write of one never overwrites the other. Its layout
 * carries `paneShare`, the pane's share of the frame, so the width travels to a screen of another size.
 */
export const LIST_PANE_WIDTH_SCOPE_KEY = 'listPaneWidth';
/** The pane's width where the person has dragged nothing: a third (decision 0408). */
export const LIST_PANE_DEFAULT_SHARE = 1 / 3;
/** 🔒 The pane's floor, which holds its primary column; `--list-pane-min-width` in the frame's stylesheet carries the same figure. */
export const LIST_PANE_MIN_PX = 288;
/** 🔒 The record's narrowest readable width beside the pane, the pane's ceiling; `--list-record-min-width` beside it carries the same figure. */
export const LIST_RECORD_MIN_PX = 480;
/** The seam between the pane and the record, the frame's own gap (`--space-3`). */
export const LIST_PANE_SEAM_PX = 12;
/** 🔒 One arrow press on the handle moves the pane this far (decision 7781). */
export const LIST_PANE_KEY_STEP_PX = 24;

/**
 * 🔒 A share held between the floor and the ceiling for a frame this wide (decision 7781): the floor
 * keeps 288 pixels, the ceiling leaves the record 480 and the seam, and where both cannot hold the
 * floor wins. A frame not yet measured keeps the share as it is, between nought and one.
 */
export function clampListPaneShare(share: number, frameWidth: number): number {
  const asked = Number.isFinite(share) ? share : LIST_PANE_DEFAULT_SHARE;
  if (!(frameWidth > 0)) return Math.min(Math.max(asked, 0.05), 0.95);
  const floor = LIST_PANE_MIN_PX;
  const ceiling = Math.max(floor, frameWidth - LIST_RECORD_MIN_PX - LIST_PANE_SEAM_PX);
  const width = Math.min(Math.max(asked * frameWidth, floor), ceiling);
  return Math.round((width / frameWidth) * 10_000) / 10_000;
}

export function itemTableScopeKey(documentType: string): string {
  return `itemTable:${documentType}`;
}

/**
 * The sidebar's collapsed state and the person's own folding of its sections,
 * both on the nav scope's layout (decision 1441; T692). `groups` names only the
 * sections the person has folded or opened by hand; a section it does not name
 * opens where it holds the page being read and folds otherwise.
 */
export interface NavPreference {
  collapsed: boolean;
  groups: Readonly<Record<string, boolean>>;
}

/** What the layout readers below actually read, so a caller can hand them a stored row of any wider shape (the wire holds the column as JSON). */
export interface LayoutBearing {
  layout?: unknown;
}

const stringsOf = (value: unknown): string[] | undefined => (Array.isArray(value) ? value.filter((one): one is string => typeof one === 'string') : undefined);

/** A stored layout as its narrow type, keeping only the keys that hold the shape each is read as, or an empty layout where the column holds anything else. */
export function readSavedLayout(value: unknown): SavedLayout {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return {};
  const raw = value as Record<string, unknown>;
  const layout: { -readonly [K in keyof SavedLayout]: SavedLayout[K] } = {};
  if (typeof raw.collapsed === 'boolean') layout.collapsed = raw.collapsed;
  if (raw.groups !== null && typeof raw.groups === 'object' && !Array.isArray(raw.groups)) {
    const groups: Record<string, boolean> = {};
    for (const [key, open] of Object.entries(raw.groups as Record<string, unknown>)) if (typeof open === 'boolean') groups[key] = open;
    layout.groups = groups;
  }
  const order = stringsOf(raw.order);
  if (order) layout.order = order;
  const hidden = stringsOf(raw.hidden);
  if (hidden) layout.hidden = hidden;
  if (typeof raw.content === 'string') layout.content = raw.content;
  if (typeof raw.paneShare === 'number' && raw.paneShare > 0 && raw.paneShare < 1) layout.paneShare = raw.paneShare;
  return layout;
}

/** A stored view's conditions, the rows whose parts are all the strings and the comparator a condition holds; anything else is dropped. */
export function readSavedCriteria(value: unknown): CriteriaRow[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((one): CriteriaRow[] => {
    if (one === null || typeof one !== 'object') return [];
    const row = one as Record<string, unknown>;
    const comparator = (VIEW_COMPARATORS as readonly unknown[]).includes(row.comparator) ? (row.comparator as CriteriaRow['comparator']) : null;
    if (typeof row.id !== 'string' || typeof row.field !== 'string' || typeof row.value !== 'string' || comparator === null) return [];
    return [{ id: row.id, field: row.field, comparator, value: row.value }];
  });
}

export function readNavPreference(row: LayoutBearing | null | undefined): NavPreference {
  const layout = readSavedLayout(row?.layout);
  return { collapsed: layout.collapsed === true, groups: layout.groups ?? {} };
}

/**
 * The list pane's collapsed state on a list-and-detail page (decision 0408),
 * read off the pane scope's layout. An absent row reads as expanded, so a
 * person who has never touched the control meets the list open.
 */
export interface ListPanePreference {
  collapsed: boolean;
}

export function readListPanePreference(row: LayoutBearing | null | undefined): ListPanePreference {
  return { collapsed: readSavedLayout(row?.layout).collapsed === true };
}

/** 🔒 The person's pane width as a share of the frame, or null where they never dragged it or pressed it back to the third (decision 7781). */
export function readListPaneShare(row: LayoutBearing | null | undefined): number | null {
  return readSavedLayout(row?.layout).paneShare ?? null;
}

/**
 * 🔨 Collapsing the list pane is a distinct rendering mode, not a width
 * transition on the same markup — the rule the navigation rail already keeps
 * (piece 12; decision 0408). The collapsed bar renders its own markup and is
 * never the pane's head squeezed narrower.
 */
export type ListPaneRendering = 'pane' | 'bar' | 'withheld';

/**
 * 🔒 A list whose read the server refused, beside a record the viewer opened,
 * draws neither the pane nor the bar (T218; decision 0693). The product owner,
 * 2026-09-24, of an outside login on their own company: *"hide the Access denied
 * part just collapse the list and direct link to the page like it did."*
 *
 * 🔨 Withheld is not collapsed. A collapsed pane expands again; a withheld one has
 * no expand control, because what it would open onto is a list the person was
 * never meant to see, and a refusal one click away is the Access Denied the
 * owner asked to be rid of. It is derived from the refusal on every visit and
 * never written to the person's preference row.
 */
export function listPaneRendering(collapsed: boolean, refused = false): ListPaneRendering {
  if (refused) return 'withheld';
  return collapsed ? 'bar' : 'pane';
}

/** The dashboard arrangement, which lives on the dashboard scope's layout. */
export function readDashboardArrangement(row: LayoutBearing | null | undefined): string[] | null {
  const order = readSavedLayout(row?.layout).order;
  return order ? [...order] : null;
}

/** The tiles a person removed from their own dashboard, kept on the same arrangement row (decision 0264). */
export function readDashboardHidden(row: LayoutBearing | null | undefined): string[] {
  return [...(readSavedLayout(row?.layout).hidden ?? [])];
}

/** A person reads their own rows and nobody else's; the store has no shared scope. */
export function rowsFor(rows: readonly SavedViewRow[], personId: string, scopeKey: string): SavedViewRow[] {
  return rows.filter((r) => r.ownerPersonId === personId && r.scopeKey === scopeKey);
}

/** The view a page opens on: the person's default for that scope, or none. */
export function defaultView(rows: readonly SavedViewRow[], personId: string, scopeKey: string): SavedViewRow | null {
  return rowsFor(rows, personId, scopeKey).find((r) => r.isDefault) ?? null;
}

/**
 * 🔒 The switcher's All view, and the address's word for it (decision 2171):
 * a person who picks All on a list with a default view keeps All on a reload.
 */
export const ALL_VIEW_ID = 'all';

/** 🔒 The chosen view rides in the list's address under this name (decision 2171). */
export const SAVED_VIEW_PARAM = 'savedView';
/** 🔨 The address key a list drawn in a pane writes, so two lists on one page never share `savedView` (T1411). */
export const SAVED_VIEW_PANE_PARAM = 'paneView';

/**
 * 🔒 The view a list shows (decision 2171): the one its address names, where the
 * person owns it; All where the address says All or names a view the reader
 * does not own; and the person's default where the address names nothing.
 * 🔒 A built-in view the list declares is everybody's, so the address naming
 * its key opens it for any reader (decision 2880).
 */
export function effectiveViewId(rows: readonly { id: string; isDefault?: boolean }[], addressed: string | null, builtInKeys: readonly string[] = []): string {
  if (addressed === ALL_VIEW_ID) return ALL_VIEW_ID;
  if (addressed !== null) return rows.some((row) => row.id === addressed) || builtInKeys.includes(addressed) ? addressed : ALL_VIEW_ID;
  return rows.find((row) => row.isDefault)?.id ?? ALL_VIEW_ID;
}

/**
 * 🔒 The switcher lists the starred views first in name order, then the rest as
 * they were made (decision 2171); All leads both, drawn by the list itself.
 */
export function switcherOrder<V extends { name: string; isFavourite?: boolean }>(rows: readonly V[]): V[] {
  const starred = rows.filter((row) => row.isFavourite).sort((a, b) => a.name.localeCompare(b.name));
  return [...starred, ...rows.filter((row) => !row.isFavourite)];
}

/** A view's stored sort, as the List Viewer reads one, or none where the row holds anything else (decision 2174). */
export function readSavedSort(value: unknown): { key: string; direction: 'asc' | 'desc' } | null {
  const sort = value as { key?: unknown; direction?: unknown } | null | undefined;
  if (!sort || typeof sort.key !== 'string' || sort.key === '') return null;
  return { key: sort.key, direction: sort.direction === 'desc' ? 'desc' : 'asc' };
}

/**
 * 🔒 A list's own scope where it is not a record list (decision 2174): one string
 * per list or record-page table, beside the record lists' `list:` routes.
 */
export function viewerScopeKey(list: string): string {
  return `viewer:${list}`;
}
